import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  QueryOperator,
  ExactMatchQueryOperators,
  EXACT_MATCH_QUERY_OPERATORS,
  VqlQuery,
  Filters,
} from "types";

import QueryModalOperators from "views/components/QueryModal/QueryModalOperators";
import QueryModalTitle from "views/components/QueryModal/QueryModalTitle";
import { convertAPIOperator } from "helpers/utility/queryFilterHelpers";
import { getSelectedQueryModal } from "redux/SingleDataset/selectors";
import {
  createAndUpdateIssueTypesFilter,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";
import {
  setSelectedQueryModal,
  updateSelectedQueryModal,
} from "redux/SingleDataset/actions";
import RangeInput from "views/components/QueryModal/RangeInput";
import QueryModal from "views/components/QueryModal";
import useVqlParams from "hooks/useVqlParams";
import styles from "./style.module.scss";
import LabelOutliersIcon from "assets/icons/LabelOutliersIcon";
import { LABEL_OUTLIER_QUERY_MIN_VALUE } from "../../../../../../helpers/constants/queries";

interface LabelOutliersQueryModalProps {
  editQueryId?: string;
}

export default function LabelOutliersQueryModal({
  editQueryId,
}: LabelOutliersQueryModalProps) {
  const { findVqlQuery, addVqlQuery, updateVqlQuery, vqlQueries } =
    useVqlParams();
  const selectedModalData = useSelector((state) =>
    getSelectedQueryModal(state),
  );

  const issueQuery = editQueryId ? findVqlQuery(editQueryId, "issues") : null;
  const oldQuery =
    issueQuery?.issues.value === "label_outlier" ? issueQuery : null;

  const queryOperator = oldQuery
    ? convertAPIOperator(oldQuery.issues.mode)
    : QueryOperator.IS;

  const range: [number, number] = oldQuery
    ? [oldQuery.issues.confidence_min, oldQuery.issues.confidence_max]
    : [LABEL_OUTLIER_QUERY_MIN_VALUE, 1];

  const modalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const existingQuery = useMemo(() => {
    if (editQueryId) {
      return null;
    }
    return (
      vqlQueries.find(
        (query: VqlQuery) =>
          isVqlQuery(query, "issues") &&
          query.issues.value === "label_outlier" &&
          convertAPIOperator(query.issues?.mode) === queryOperator,
      ) || null
    );
  }, [vqlQueries, queryOperator, editQueryId]);

  useEffect(() => {
    const query = vqlQueries.find(
      (query: VqlQuery) =>
        isVqlQuery(query, "issues") &&
        query.issues?.value === "label_outlier" &&
        convertAPIOperator(query.issues?.mode) === queryOperator,
    );
    if (query) {
      dispatch(
        updateSelectedQueryModal({ type: Filters.LABEL_OUTLIER, id: query.id }),
      );
    } else {
      submitQuery(queryOperator as ExactMatchQueryOperators, range);
      dispatch(updateSelectedQueryModal({ type: Filters.LABEL_OUTLIER }));
    }
    // eslint-disable-next-line
  }, [queryOperator]);

  const submitQuery = (
    operator: ExactMatchQueryOperators,
    newRange: [number, number],
  ) => {
    const queryId = editQueryId || existingQuery?.id;
    const vqlQuery = createAndUpdateIssueTypesFilter(
      "label_outlier",
      operator,
      newRange,
      queryId,
    );
    queryId ? updateVqlQuery(vqlQuery) : addVqlQuery(vqlQuery);
    if (selectedModalData) {
      dispatch(
        setSelectedQueryModal({ ...selectedModalData, id: vqlQuery.id }),
      );
    }
  };

  const handleOptionSelection = (operator: QueryOperator) => {
    if (operator === queryOperator) return;

    submitQuery(operator as ExactMatchQueryOperators, range);
  };

  const handleSubmission = (value: number[]) => {
    if (value[0] === range[0] && value[1] === range[1]) return;

    submitQuery(queryOperator as ExactMatchQueryOperators, [
      value[0],
      value[1],
    ]);
  };

  return (
    <QueryModal modalRef={modalRef} keepOpenOnLoading>
      <QueryModalTitle
        icon={<LabelOutliersIcon />}
        info="Identifies mislabeled examples with no correct class in the vocabulary, misplaced bounding boxes or examples that are out-of-distribution for the class."
      >
        Class Outliers
      </QueryModalTitle>
      <div className={styles.filterContent}>
        <QueryModalOperators
          operators={[...EXACT_MATCH_QUERY_OPERATORS]}
          selectedOperator={queryOperator}
          onSelect={handleOptionSelection}
        />
        <RangeInput
          range={range}
          title="Confidence:"
          onChange={handleSubmission}
          minValue={LABEL_OUTLIER_QUERY_MIN_VALUE}
        />
      </div>
    </QueryModal>
  );
}
